import React, { useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ManagementReport } from '@/components/ManagementReport';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Wrench, 
  TrendingUp, 
  Clock,
  AlertTriangle,
  AlertCircle,
  Activity,
  FileText,
  CalendarClock
} from 'lucide-react';
import { 
  assets, 
  workOrders, 
  faults,
  costHistory,
  getBuildingById,
  bozpIncidents 
} from '@/data/mockData';
import { getMergedFaults, getMergedWorkOrders } from '@/data/faultStore';
import { getMergedIncidents } from '@/data/incidentStore';
import { canViewManagementReport } from '@/lib/permissions';

const StatCard = ({ title, value, subtitle, icon: Icon, trend, variant = 'default' }) => {
  const variantStyles = {
    default: 'border-border',
    success: 'border-success border-2',
    warning: 'border-warning border-2',
    danger: 'border-destructive border-2',
    info: 'border-info border-2',
  };

  return (
    <Card className={`shadow-soft ${variantStyles[variant]}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-bold text-foreground">{value}</p>
              {trend && (
                <span className={`text-sm font-medium ${
                  trend.startsWith('+') ? 'text-success' : 'text-destructive'
                }`}>
                  {trend}
                </span>
              )}
            </div>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          {Icon && (
            <div className="p-3 bg-primary-light rounded-lg">
              <Icon className="w-6 h-6 text-primary" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const getStatusColor = (status) => {
  const colors = {
    operational: 'success',
    maintenance: 'warning',
    critical: 'destructive',
    offline: 'destructive',
    open: 'default',
    inProgress: 'info',
    completed: 'success',
  };
  return colors[status] || 'default';
};

const getPriorityColor = (priority) => {
  const colors = {
    high: 'destructive',
    medium: 'warning',
    low: 'default',
  };
  return colors[priority] || 'default';
};

const CHART_HEIGHT = 300;

/** Sloupcový graf OPEX/CAPEX po měsících – pevná výška sloupců v px. */
const BAR_AREA_HEIGHT = 200;

const SimpleBarChart = ({ data }) => {
  const maxVal = Math.max(1, ...data.flatMap(d => [d.OPEX || 0, d.CAPEX || 0]));
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-end justify-around gap-2 px-1" style={{ height: BAR_AREA_HEIGHT }}>
        {data.map((d) => {
          const opexH = Math.max(6, (BAR_AREA_HEIGHT * (d.OPEX || 0)) / maxVal);
          const capexH = Math.max(6, (BAR_AREA_HEIGHT * (d.CAPEX || 0)) / maxVal);
          return (
            <div key={d.month} className="flex-1 flex flex-col items-center gap-2 min-w-0 max-w-[18%]">
              <div className="w-full flex gap-1 justify-center items-end" style={{ height: BAR_AREA_HEIGHT }}>
                <div
                  className="flex-1 min-w-[12px] max-w-[24px] rounded-t-sm bg-primary shadow-sm"
                  style={{ height: opexH }}
                  title={`OPEX: ${(d.OPEX || 0).toFixed(0)}k Kč`}
                />
                <div
                  className="flex-1 min-w-[12px] max-w-[24px] rounded-t-sm bg-primary/60 shadow-sm"
                  style={{ height: capexH }}
                  title={`CAPEX: ${(d.CAPEX || 0).toFixed(0)}k Kč`}
                />
              </div>
              <span className="text-xs text-muted-foreground truncate w-full text-center font-medium">{d.month}</span>
            </div>
          );
        })}
      </div>
      <div className="flex gap-6 mt-3 pt-3 border-t border-border text-xs text-muted-foreground shrink-0">
        <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded-sm bg-primary shrink-0" /> OPEX</span>
        <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded-sm bg-primary/60 shrink-0" /> CAPEX</span>
      </div>
    </div>
  );
};

/** Graf podílu stavů aktiv – popis nahoře, segmentovaný pruh víc dole. */
const SimpleAssetHealthChart = ({ data }) => {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  return (
    <div className="h-full flex flex-col justify-center gap-5">
      <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm shrink-0">
        {data.map((d, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-md shrink-0 shadow-sm" style={{ backgroundColor: d.color }} />
            <span className="text-muted-foreground"><span className="font-medium text-foreground">{d.name}</span>: {d.value}</span>
          </li>
        ))}
      </ul>
      <div className="flex h-8 w-full rounded-lg overflow-hidden bg-muted shadow-inner border border-border/50 mt-auto">
        {data.map((d, i) => (
          <div
            key={i}
            className="h-full transition-opacity hover:opacity-90 flex items-center justify-center"
            style={{ width: `${(100 * d.value) / total}%`, backgroundColor: d.color, minWidth: d.value > 0 ? 24 : 0 }}
            title={`${d.name}: ${d.value}`}
          >
            {d.value > 0 && (100 * d.value) / total > 12 && (
              <span className="text-xs font-medium text-white drop-shadow-md">{d.value}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const DashboardPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [reportOpen, setReportOpen] = useState(false);

  const allWorkOrders = getMergedWorkOrders(workOrders);
  const allFaults = getMergedFaults(faults);
  const openWorkOrders = allWorkOrders.filter(wo => wo.status === 'open' || wo.status === 'inProgress').length;
  const totalOpex = costHistory[costHistory.length - 1]?.opex || 0;
  const avgRepairTime = '4.5';

  const replaceRecommendations = useMemo(() => {
    const criticalAssets = assets.filter(a => a.status === 'critical' || a.status === 'maintenance').slice(0, 6);
    return criticalAssets.map(asset => {
      const assetWOs = allWorkOrders.filter(wo => wo.assetId === asset.id);
      const repairCostTotal = assetWOs.reduce((sum, wo) => sum + (wo.opex || 0) + (wo.capex || 0), 0);
      const fallback = repairCostTotal === 0 ? asset.totalCost * 0.25 : repairCostTotal;
      const replacementCost = asset.totalCost * 1.2;
      const effective = repairCostTotal > 0 ? repairCostTotal : fallback;
      const recommendation = effective > replacementCost * 0.7 ? 'replace' : 'repair';
      return recommendation === 'replace' ? {
        assetName: asset.name,
        buildingName: getBuildingById(asset.buildingId)?.name ?? '—',
        repairCostTotal: effective,
        replacementCost,
      } : null;
    }).filter(Boolean);
  }, [allWorkOrders]);

  const bozpSummary = useMemo(() => {
    const all = getMergedIncidents(bozpIncidents);
    const bySeverity = { low: 0, medium: 0, high: 0 };
    all.forEach(inc => {
      if (inc.severity) bySeverity[inc.severity] = (bySeverity[inc.severity] || 0) + 1;
    });
    return { total: all.length, bySeverity };
  }, []);

  const assetStatusData = [
    { name: t('operational'), value: assets.filter(a => a.status === 'operational').length, color: 'hsl(var(--success))' },
    { name: t('maintenance'), value: assets.filter(a => a.status === 'maintenance').length, color: 'hsl(var(--warning))' },
    { name: t('critical'), value: assets.filter(a => a.status === 'critical').length, color: 'hsl(var(--destructive))' },
  ];

  const costTrendData = costHistory.map(item => ({
    month: item.month,
    OPEX: item.opex / 1000,
    CAPEX: item.capex / 1000,
  }));

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const in30Days = new Date(today);
  in30Days.setDate(in30Days.getDate() + 30);
  const in14Days = new Date(today);
  in14Days.setDate(in14Days.getDate() + 14);

  const criticalAssets = assets.filter(a => a.status === 'critical' || a.status === 'maintenance');
  const overdueWorkOrders = allWorkOrders.filter(wo => {
    if (wo.status === 'completed') return false;
    const due = new Date(wo.dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  });
  const needsAttentionItems = [
    ...criticalAssets.slice(0, 3).map(a => ({ type: 'asset', id: a.id, label: a.name, sub: getBuildingById(a.buildingId)?.name, status: a.status })),
    ...overdueWorkOrders.slice(0, 3).map(wo => ({ type: 'wo', id: wo.id, label: wo.title, sub: `Splatnost: ${new Date(wo.dueDate).toLocaleDateString()}`, status: 'overdue' })),
  ].slice(0, 5);

  const upcomingMaintenanceAssets = assets.filter(a => {
    const next = new Date(a.nextMaintenance);
    next.setHours(0, 0, 0, 0);
    return next >= today && next <= in30Days;
  });
  const upcomingDueWorkOrders = allWorkOrders.filter(wo => {
    if (wo.status === 'completed') return false;
    const due = new Date(wo.dueDate);
    due.setHours(0, 0, 0, 0);
    return due >= today && due <= in14Days;
  });
  const upcomingItems = [
    ...upcomingMaintenanceAssets.slice(0, 3).map(a => ({ type: 'asset', id: a.id, label: a.name, sub: `Údržba: ${new Date(a.nextMaintenance).toLocaleDateString()}`, status: null })),
    ...upcomingDueWorkOrders.slice(0, 3).map(wo => ({ type: 'wo', id: wo.id, label: wo.title, sub: `Termín: ${new Date(wo.dueDate).toLocaleDateString()}`, status: wo.status })),
  ].slice(0, 5);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {t('welcome')}, {user?.name}
          </h1>
          <p className="text-muted-foreground">{t('overview')}</p>
        </div>
        {canViewManagementReport(user?.role) && (
          <Button onClick={() => setReportOpen(true)} className="gap-2 shrink-0">
            <FileText className="w-4 h-4" />
            {t('reportForManagement')}
          </Button>
        )}
      </div>

      {canViewManagementReport(user?.role) && (
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="sr-only">{t('reportForManagement')}</DialogTitle>
          <ManagementReport
            replaceRecommendations={replaceRecommendations}
            bozpSummary={bozpSummary}
            onClose={() => setReportOpen(false)}
          />
        </DialogContent>
      </Dialog>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('totalAssets')}
          value={assets.length}
          subtitle={t('statsSubtitleBuildings')}
          icon={Building2}
          variant="info"
        />
        <StatCard
          title={t('openWorkOrders')}
          value={openWorkOrders}
          subtitle={t('statsSubtitleAttention')}
          icon={Wrench}
          variant="warning"
        />
        <StatCard
          title={t('monthlyOpex')}
          value={`${(totalOpex / 1000).toFixed(0)}k Kč`}
          trend="+12%"
          subtitle={t('statsSubtitleVsMonth')}
          icon={TrendingUp}
        />
        <StatCard
          title={t('avgRepairTime')}
          value={`${avgRepairTime}h`}
          trend="-8%"
          subtitle={t('statsSubtitleImprovement')}
          icon={Clock}
          variant="success"
        />
      </div>

      {/* Vyžaduje pozornost & Nadcházející údržba */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-soft border-warning border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              {t('needsAttention')}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {t('needsAttentionSub')}
            </p>
          </CardHeader>
          <CardContent>
            {needsAttentionItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('noItems')}</p>
            ) : (
              <ul className="space-y-2">
                {needsAttentionItems.map((item) => (
                  <li key={`${item.type}-${item.id}`} className="flex items-center justify-between gap-2 p-2 rounded-md bg-muted/50">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.sub}</p>
                    </div>
                    <Badge variant={item.status === 'critical' ? 'destructive' : item.status === 'overdue' ? 'destructive' : 'warning'}>
                      {item.status === 'overdue' ? t('overdue') : item.status === 'critical' ? t('critical') : t('maintenance')}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
            <Button variant="outline" size="sm" className="mt-3 w-full" asChild>
              <Link to="/assets">{t('assets')}</Link>
            </Button>
          </CardContent>
        </Card>
        <Card className="shadow-soft border-info border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-info" />
              {t('upcomingMaintenance')}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {t('upcomingMaintenanceSub')}
            </p>
          </CardHeader>
          <CardContent>
            {upcomingItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('noItems')}</p>
            ) : (
              <ul className="space-y-2">
                {upcomingItems.map((item) => (
                  <li key={`${item.type}-${item.id}`} className="flex items-center justify-between gap-2 p-2 rounded-md bg-muted/50">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.sub}</p>
                    </div>
                    {item.status && (
                      <Badge variant={getStatusColor(item.status)}>{t(item.status)}</Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <Button variant="outline" size="sm" className="mt-3 w-full" asChild>
              <Link to="/work-orders">{t('workOrders')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Trends */}
        <Card className="shadow-soft overflow-hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              {t('costTrends')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full" style={{ height: CHART_HEIGHT, minHeight: CHART_HEIGHT }}>
              {costTrendData.length > 0 ? (
                <SimpleBarChart data={costTrendData} />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">{t('noData')}</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Asset Health */}
        <Card className="shadow-soft overflow-hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              {t('assetHealth')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full" style={{ height: CHART_HEIGHT, minHeight: CHART_HEIGHT }}>
              {assetStatusData.some(d => d.value > 0) ? (
                <SimpleAssetHealthChart data={assetStatusData} />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">{t('noData')}</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Faults */}
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              {t('recentFaults')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {allFaults.slice(0, 4).map((fault) => (
                <div key={fault.id} className="flex items-start gap-3 p-3 bg-muted rounded-lg hover:bg-secondary transition-colors">
                  <AlertTriangle className="w-5 h-5 text-warning mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {fault.description}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(fault.reportedDate).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant={getStatusColor(fault.status)}>
                    {fault.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Open Work Orders */}
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-primary" />
              {t('openWorkOrders')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {allWorkOrders.filter(wo => wo.status !== 'completed').slice(0, 4).map((wo) => (
                <div key={wo.id} className="flex items-start gap-3 p-3 bg-muted rounded-lg hover:bg-secondary transition-colors">
                  <Wrench className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-foreground truncate">
                        {wo.title}
                      </p>
                      <Badge variant={getPriorityColor(wo.priority)} className="text-xs">
                        {t(wo.priority)}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {wo.assignedTo} • Due: {new Date(wo.dueDate).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant={getStatusColor(wo.status)}>
                    {t(wo.status)}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
